import type { CompanyProfile } from "@/lib/types";

/** Campos de endereço de um perfil comercial — o endereço mora quebrado em partes, não num campo único. */
type CompanyAddressFields = Pick<
  CompanyProfile,
  | "addressCep"
  | "addressLogradouro"
  | "addressNumero"
  | "addressComplemento"
  | "addressBairro"
  | "addressCidade"
  | "addressUf"
>;

const clean = (value?: string | null): string | null => {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Compõe o endereço completo de um perfil comercial a partir dos campos separados
 * (mesma fonte usada pelo formulário de edição), juntando apenas as partes preenchidas
 * para não deixar separadores soltos. Retorna `null` quando não há nenhuma informação.
 */
export function formatCompanyAddress(profile: CompanyAddressFields | null | undefined): string | null {
  if (!profile) return null;

  const logradouro = clean(profile.addressLogradouro);
  const numero = clean(profile.addressNumero);
  const complemento = clean(profile.addressComplemento);
  const bairro = clean(profile.addressBairro);
  const cidade = clean(profile.addressCidade);
  const uf = clean(profile.addressUf);
  const cep = clean(profile.addressCep);

  const street = logradouro
    ? numero
      ? `${logradouro}, nº ${numero}`
      : logradouro
    : numero
      ? `nº ${numero}`
      : null;
  const cityUf = cidade ? (uf ? `${cidade}/${uf}` : cidade) : uf;
  const cepPart = cep ? `CEP ${cep}` : null;

  const segments = [street, complemento, bairro, cityUf, cepPart].filter(
    (segment): segment is string => segment !== null,
  );

  return segments.length > 0 ? segments.join(" · ") : null;
}
